// Copied from apps/admin/src/features/map-view/model/geofence-polygons.ts (PRD #875).
const isPosition = (v: unknown): v is [number, number] =>
  Array.isArray(v) && typeof v[0] === "number" && typeof v[1] === "number";

/**
 * Rings as [lat, lon], from GeoJSON [lon, lat]. Depth-normalised so Polygon and
 * MultiPolygon share a path; empty rings yield none and fall back to a marker.
 */
const toRing = (node: unknown): [number, number][] | null => {
  if (!Array.isArray(node) || !isPosition(node[0])) return null;
  const points = node.filter(isPosition).map((c) => [c[1], c[0]] as [number, number]);
  return points.length >= 3 ? points : null;
};

/**
 * Polygons as [lat, lon], from GeoJSON [lon, lat]. Grouping matters: Leaflet
 * reads a flat ring list as one outline plus holes, so a MultiPolygon's second
 * part would be punched out of its first.
 */
export const pickupLocationPolygons = (geometry: unknown): [number, number][][][] => {
  const source = geometry as {
    type?: unknown;
    coordinates?: unknown;
    geometry?: { type?: unknown; coordinates?: unknown };
  };
  const coordinates = source?.geometry?.coordinates ?? source?.coordinates;
  if (!Array.isArray(coordinates)) return [];
  // The depth check below would read a LineString's bare position list as a
  // ring; `type` is consulted only to rule that out.
  const type = source?.geometry?.type ?? source?.type;
  if (typeof type === "string" && type.endsWith("LineString")) return [];

  // Depth tells the type apart without trusting `type`: a ring's first element
  // is a position, a polygon's is a ring, a multipolygon's is a polygon.
  const asPolygon = (node: unknown): [number, number][][] => {
    if (!Array.isArray(node)) return [];
    const rings = node.map(toRing).filter((r): r is [number, number][] => r !== null);
    return rings;
  };

  const single = toRing(coordinates);
  if (single) return [[single]];

  const first = (coordinates as unknown[])[0];
  if (Array.isArray(first) && isPosition((first as unknown[])[0])) {
    const polygon = asPolygon(coordinates);
    return polygon.length > 0 ? [polygon] : [];
  }

  return (coordinates as unknown[]).map(asPolygon).filter((p) => p.length > 0);
};

export const polygonsBounds = (
  polygons: [number, number][][][],
): { minLat: number; maxLat: number; minLon: number; maxLon: number } | null => {
  let minLat = Infinity,
    maxLat = -Infinity,
    minLon = Infinity,
    maxLon = -Infinity;
  for (const polygon of polygons) {
    for (const ring of polygon) {
      for (const [lat, lon] of ring) {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
      }
    }
  }
  return minLat === Infinity ? null : { minLat, maxLat, minLon, maxLon };
};

/** Bounding-box centre; stable enough to hang a label on, unlike a true centroid. */
export const polygonsCenter = (polygons: [number, number][][][]): [number, number] => {
  const b = polygonsBounds(polygons);
  if (!b) return [0, 0];
  return [(b.minLat + b.maxLat) / 2, (b.minLon + b.maxLon) / 2];
};
